<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Cutoff Types"
      text="Update cutoff type"
      class="mb-0"
      :loading="loading"
    > 
      <form v-if="dialog && !loading">
        <v-container>
          <v-row no-gutters>
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-text-field
                v-model="localCutoffType.name"
                v-validate="'required'"
                label="Name"
                :error-messages="errors.collect('name')"    
                data-vv-name="name"
                outlined
                data-vv-as="name"
              />
            </v-col>

            <v-col
              cols="12"
              class="mb-4"
            >
              <tiptap-vuetify
                v-model="localCutoffType.description"
                class="full-width text-editor-toolbar-height pb-1"
                placeholder="Description"
                :extensions="extensions"
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localCutoffType.type" 
                v-validate="'required'"
                :error-messages="errors.collect('type')"    
                data-vv-name="type" 
                data-vv-as="cutoff type"
                label="Cutoff Type"
                item-text="name"
                item-value="name"
                outlined
                :items="types"
                readonly
                filled
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0 pr-2"
              sm="12"
              md="9"
              lg="9"  
              xl="9"
            >
              <v-autocomplete
                v-model="localCutoffType.start_period" 
                v-validate="'required'"
                :error-messages="errors.collect('start_period')"    
                data-vv-name="start_period" 
                data-vv-as="start period"
                label="Start Period"
                item-text="name"
                item-value="name"
                outlined
                :items="localCutoffType.type === 'monthly' ? monthPeriods : weekPeriods"
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0"
              sm="12"
              md="3"
              lg="3"  
              xl="3"
            >
              <v-text-field
                v-model="localCutoffType.start_day" 
                :rules="[rules.startDayRequired,rules.min,rules.max]"
                label="Start Day"
                type="number"
                outlined
                @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0 pr-2"
              sm="12"
              md="9"
              lg="9"  
              xl="9"
            >
              <v-autocomplete
                v-model="localCutoffType.end_period" 
                v-validate="'required'"
                :error-messages="errors.collect('end_period')"    
                data-vv-name="end_period" 
                data-vv-as="end period"
                label="End Period"
                item-text="name"
                item-value="name"
                :items="localCutoffType.type === 'monthly' ? monthPeriods : weekPeriods"
                outlined
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0"
              sm="12"
              md="3"
              lg="3"  
              xl="3"
            >
              <v-text-field
                v-model="localCutoffType.end_day" 
                :rules="[rules.endDayRequired,rules.min,rules.max]"
                label="End Day"
                type="number"
                outlined
                @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>
          </v-row>
        </v-container> 
      </form> 
      
      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary" 
          class="white--text px-10"         
          @click="submitForm"
        >
          Save
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { TiptapVuetify, Heading, Bold, Italic, Underline, BulletList, OrderedList, ListItem, Link, HardBreak } from 'tiptap-vuetify'

  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    components: {
      TiptapVuetify,
    },
    props: {
      cutoffType: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        loading: false,
        dialog: false,        
        progressing: false,
        localCutoffType: null,    
        types: [
          { name: 'monthly' }, 
          { name: 'weekly' },
        ],
        monthPeriods: [
          { name: 'current_month' }, 
          { name: 'previous_month' },
        ],
        weekPeriods: [
          { name: 'current_week' }, 
          { name: 'previous_week' },
        ],
        rules: {
          startDayRequired: value => !!value || 'The start day field is required',     
          endDayRequired: value => !!value || 'The end day field is required',     
          min: value => value >= 1 || 'Minimum number is 1',      
          max: value => this.localCutoffType.type === 'monthly' ? (value <= 31 || 'Maximum number is 31') : (value <= 7 || 'Maximum number is 7'),      
        },  
        extensions: [
          Link,
          Underline,
          Italic,
          ListItem,
          BulletList,
          OrderedList,
          [Heading, {
            options: {
              levels: [1, 2, 3],
            },
          }],
          Bold,
          HardBreak,
        ],
      }
    },
    watch: {
      dialog (value) {
        if (!value) return this.$resetData()
        
        this.loading = true

        this.localCutoffType = { ...this.cutoffType }

        this.loading = false
      },
    },
    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true

            this.$api.cutoffTypes().update(this.cutoffType.id, this.prepareFormData())
              .then((res) => {
                this.$emit('updated')                
                this.progressing = false
                this.dialog = false
              })
              .catch(() => {
                this.progressing = false
              })
          })
      },   
   
      prepareFormData () {
        return {
          name: this.localCutoffType.name,
          description: this.localCutoffType.description,
          type: this.localCutoffType.type,
          start_period: this.localCutoffType.start_period,
          start_day: this.localCutoffType.start_day,
          end_period: this.localCutoffType.end_period,
          end_day: this.localCutoffType.end_day,
        }
      },
    },
  }
</script>

<style scoped>
.text-editor-toolbar-height>>> .v-toolbar__content{
  height: unset !important;
  min-height: 42px;
}
.text-editor-toolbar-height>>>.v-sheet.v-sheet--tile.theme--light.v-toolbar.v-toolbar--dense.v-toolbar--flat.grey.lighten-4{
  min-height:unset !important;
}
.full-width{
  width: 100%;
}
</style>
